import { ANALYTICS_API_URL, ANALYTICS_ENABLED, DEBUG } from "@/lib/config";
import { getAuthToken } from "@/lib/auth";
import { isBrowser } from "./browser";

export const ANALYTICS_REQUEST_TIMEOUT_MS = 4000;

/**
 * Ingestion is unauthenticated by design, but a signed-in visitor can prove who
 * they are. Without this the service has only the body's userId to go on, which
 * anyone could set to anyone, so it discards it and the event stays anonymous.
 *
 * Never throws: storage can be disabled and analytics must not break a page.
 */
function readAuthToken(): string | null {
  try {
    return getAuthToken() ?? null;
  } catch {
    return null;
  }
}

export async function postAnalyticsJson(
  path: string,
  body: unknown,
): Promise<boolean> {
  if (!ANALYTICS_ENABLED || !isBrowser()) return false;
  const base = ANALYTICS_API_URL.replace(/\/$/, "");
  if (!base) return false;

  const controller = new AbortController();
  const timer = window.setTimeout(
    () => controller.abort(),
    ANALYTICS_REQUEST_TIMEOUT_MS,
  );

  try {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    const token = readAuthToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${base}${path}`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      signal: controller.signal,
      keepalive: true,
    });
    if (!response.ok && DEBUG) {
      console.debug("[meritlens-analytics] request failed", response.status);
    }
    return response.ok;
  } catch (error) {
    if (DEBUG) {
      console.debug("[meritlens-analytics] request error", error);
    }
    return false;
  } finally {
    window.clearTimeout(timer);
  }
}
